import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Slide,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { styles } from "./styles/NotesModal.styles"
import React, { useEffect, useState, useCallback } from "react"
import CloseIcon from "@mui/icons-material/Close"
import Image from "components/Image"
import { useTranslation } from "react-i18next"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import "./styles/quill.css"
import TextInput from "components/UI/TextInput"
import useApi from "modules/studentNotes"
import NotesItem, { uiTranslationKey } from "./NotesItem"
import { FaEdit, FaUserTag, /* FaFileExport, */ FaTrash } from "react-icons/fa"
import { VirtualKeyboard } from "components/UI"
import { useAuthContext } from "contexts/AuthContext"
import ConfirmAlert from "components/UI/ConfirmAlert"
import ShareNotes from "./ShareNotes"
import { newNote, saveNote as saveNoteAudio } from "utils/sounds"
import useTour from "hooks/useTour"

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

export default function NotesModal({ open, onClose, fontSettings = {} }) {
  const { t } = useTranslation()
  const { userData, audioSettings } = useAuthContext()
  const {
    getStundentNotes,
    deleteStudentNote,
    getStudentNoteUpdateDate,
    createStudentNote,
    updateStudentNote,
  } = useApi()
  const [originalNotes, setOriginalNotes] = useState([])
  const [notes, setNotes] = useState([])
  const [value, setValue] = useState("")
  const [noteTitle, setNoteTitle] = useState("")
  const [selectedItem, setSelectedItem] = useState(null)
  const [confirm, setConfirm] = useState(false)
  const [search, setSearch] = useState("")
  const [confirmChanges, setConfirmChanges] = useState(false)
  const [openShare, setOpenShare] = useState(false)
  const playNewNote = () => {
    if (audioSettings?.enabledAudioEffects) {
      const audio = new Audio(newNote)
      audio.volume = 0.5
      audio.play().catch((e) => console.log("New note audio play error:", e))
    }
  }
  const playSave = () => {
    if (audioSettings?.enabledAudioEffects) {
      const audio = new Audio(saveNoteAudio)
      audio.volume = 0.5
      audio.play().catch((e) => console.log("Save audio play error:", e))
    }
  }
  const quillRef = React.useRef(null)
  const { setIsOpen } = useTour()
  const theme = useTheme()

  useEffect(() => {
    if (open) {
      getStundentNotes().then((data) => {
        const newData = [...data]
        if (newData.length > 0) {
          newData[0].isSelected = true
          setValue(newData[0].note)
          const title =
            newData[0].noteTypeId > 0 ? newData[0].noteType : newData[0].title
          setNoteTitle(title)
          setSelectedItem(newData[0])
        }
        setOriginalNotes(newData)
        setNotes(newData)
      })
    } else {
      setNotes([])
      cleanNote()
    }
  }, [open])

  const selectItem = useCallback(
    (index) => {
      const newNotes = notes
        .filter((note) => !note.isNewNote)
        .map((note, i) => {
          if (i === index) {
            note.isSelected = true
            // console.log("🚀 ~ file: index.js:82 ~ newNotes ~ note:", note)
            setValue(note.note)
            const title = note.noteTypeId > 0 ? note.noteType : note.title
            setNoteTitle(title)
            setSelectedItem(note)
          } else {
            note.isSelected = false
          }
          return note
        })
      setNotes(newNotes)
      setOriginalNotes(newNotes)
    },
    [notes]
  )

  const addNewNote = useCallback(() => {
    // if there is already a new note, do nothing
    if (notes.find((note) => note.isNewNote)) {
      return
    }
    setValue("")
    playNewNote()
    const newItem = {
      note: "",
      title: t("new_note"),
      noteType: "custom",
      noteTypeId: 0,
      allowDelete: false,
      allowEdit: true,
      isSharedNote: false,
      sharedWithUsers: 0,
      userIdCreatedBy: 0,
      isNewNote: true,
      isSelected: true,
    }
    const oldNotes = [...notes]
    oldNotes.forEach((note) => {
      note.isSelected = false
    })
    const title = newItem.noteTypeId > 0 ? newItem.noteType : newItem.title
    setNoteTitle(title)
    setSelectedItem(newItem)
    setNotes([...oldNotes, newItem])
  }, [notes, playNewNote])

  const deleteNote = useCallback(async () => {
    // find selected note
    setConfirm(false)
    const selectedNote = notes.find((note) => note.isSelected)
    if (selectedNote) {
      const response = await deleteStudentNote(selectedNote.noteId)
      if (response) {
        cleanNote()
        getStundentNotes().then((data) => {
          setNotes(data)
        })
      }
    }
  }, [notes])

  const saveNote = useCallback(async () => {
    const item = notes.find((note) => note.isSelected)
    if (!item) return
    if (item.noteId > 0) {
      if (item.isSharedNote || item.sharedWithUsers > 0) {
        const noteLastUpdate = await getStudentNoteUpdateDate(item.noteId)
        if (noteLastUpdate?.lastUpdate !== item.lastUpdate) {
          setConfirmChanges(true)
        } else {
          updateNote(item.noteId)
        }
      } else {
        updateNote(item.noteId)
      }
    } else {
      createNewNote()
    }
  }, [notes, value, noteTitle, playSave])

  const createNewNote = useCallback(async () => {
    const item = notes.find((note) => note.isSelected)
    console.log({
      note: value,
      title: noteTitle,
      noteTypeId: item?.noteTypeId,
    })
    const response = await createStudentNote({
      note: value,
      title: noteTitle,
      noteTypeId: item?.noteTypeId,
    })
    if (response) {
      playSave()
      cleanNote()
      getStundentNotes().then((data) => {
        const newData = [...data]
        const index = newData.findIndex(
          (note) => note.noteId === response.noteId
        )
        if (index > -1) {
          newData[index].isSelected = true
          setValue(newData[index].note)
          const title =
            newData[index].noteTypeId > 0
              ? newData[index].noteType
              : newData[index].title
          setNoteTitle(title)
          setSelectedItem(newData[index])
        }
        setNotes(data)
      })
    }
  }, [value, noteTitle, notes])

  const updateNote = useCallback(
    async (noteId) => {
      const response = await updateStudentNote({
        noteId: noteId,
        note: value,
        title: noteTitle,
      })
      console.log({
        noteId: noteId,
        note: value,
        title: noteTitle,
      })
      // console.log("🚀 ~ file: index.js:163 ~ updateNote ~ response:", response)
      if (response) {
        playSave()
        getStundentNotes().then((data) => {
          const newData = [...data]
          const index = newData.findIndex(
            (note) => note.noteId === response.noteId
          )
          if (index > -1) {
            newData[index].isSelected = true
            setValue(newData[index].note)
            const title =
              newData[index].noteTypeId > 0
                ? newData[index].noteType
                : newData[index].title
            setNoteTitle(title)
            setSelectedItem(newData[index])
          }
          setNotes(data)
          setOriginalNotes(data)
        })
      }
    },
    [value, noteTitle]
  )

  const cleanNote = () => {
    setSearch("")
    setValue("")
    setNoteTitle("")
    setSelectedItem(null)
    // delete all items where isNewNote is true
    const newNotes = notes.filter((note) => !note.isNewNote)
    setNotes(newNotes)
    if (notes.length > 0) {
      selectItem(0)
    }
  }

  const onKeyPress = (key) => {
    // add key to the end of the note
    // from value extract content inside p tag
    const content = quillRef.current.getEditor().getText()
    const newValue = content + key
    setValue(newValue)
  }

  const onSearch = useCallback(() => {
    if (search === "") {
      const oldNotes = originalNotes.map((note) => {
        note.isSelected = false
        return note
      })
      setNotes(oldNotes)
      // select first note
      if (oldNotes.length > 0) {
        oldNotes[0].isSelected = true
        setValue(oldNotes[0].note)
        const title =
          oldNotes[0].noteTypeId > 0 ? oldNotes[0].noteType : oldNotes[0].title
        setNoteTitle(title)
        setSelectedItem(oldNotes[0])
      } else {
        setValue("")
        setNoteTitle("")
        setSelectedItem(null)
      }
      return
    }
    const newNotes = originalNotes
      .filter((note) => {
        return note.title.toLowerCase().includes(search.toLowerCase())
      })
      .map((note) => {
        note.isSelected = false
        return note
      })
    if (newNotes.length > 0) {
      newNotes[0].isSelected = true
      setValue(newNotes[0].note)
      const title =
        newNotes[0].noteTypeId > 0 ? newNotes[0].noteType : newNotes[0].title
      setNoteTitle(title)
      setSelectedItem(newNotes[0])
    } else {
      setValue("")
      setNoteTitle("")
      setSelectedItem(null)
    }
    setNotes(newNotes)
  }, [search, originalNotes, notes])

  const cleanSearch = useCallback(() => {
    setSearch("")
    const oldNotes = originalNotes.map((note) => {
      note.isSelected = false
      return note
    })
    // select first note
    if (oldNotes.length > 0) {
      oldNotes[0].isSelected = true
      setValue(oldNotes[0].note)
      const title =
        oldNotes[0].noteTypeId > 0 ? oldNotes[0].noteType : oldNotes[0].title
      setNoteTitle(title)
      setSelectedItem(oldNotes[0])
    } else {
      setValue("")
      setNoteTitle("")
      setSelectedItem(null)
    }
    setNotes(oldNotes)
  }, [originalNotes])

  const close = useCallback(() => {
    setIsOpen(false)
    onClose()
  }, [setIsOpen])

  return (
    <React.Fragment>
      <ShareNotes
        open={openShare}
        onClose={() => setOpenShare(false)}
        title={
          selectedItem !== null && selectedItem?.noteTypeId === 0
            ? noteTitle
            : noteTitle?.length > 0
            ? t(uiTranslationKey[selectedItem?.noteTypeId] || noteTitle)
            : noteTitle
        }
        noteId={notes.find((note) => note.isSelected)?.noteId}
        fontSettings={fontSettings}
      />
      <ConfirmAlert
        open={confirm}
        title={t("delete_note")}
        description={t("are_you_sure_delete_note")}
        cancelText={t("cancel")}
        okText={t("delete")}
        onCancelClick={() => setConfirm(false)}
        onOkClick={deleteNote}
        onClose={() => setConfirm(false)}
        fontSettings={fontSettings}
      />
      <ConfirmAlert
        open={confirmChanges}
        title={t("notes")}
        description={t(
          "There are recent changes to the note. Do you want to download the changes? Your changes will be lost."
        )}
        cancelText={t("cancel")}
        okText={t("Yes")}
        secondaryText={t("Create new Note")}
        secondaryButton={true}
        onCancelClick={() => setConfirmChanges(false)}
        onOkClick={() => {
          setConfirmChanges(false)
          getStundentNotes().then((data) => {
            setNotes(data)
            setOriginalNotes(data)
          })
        }}
        onClose={() => setConfirmChanges(false)}
        onSecondaryClick={createNewNote}
        fontSettings={fontSettings}
      />
      <Dialog
        open={open}
        onClose={close}
        TransitionComponent={Transition}
        fullWidth
        PaperProps={{
          sx: [
            styles.container,
            fontSettings?.fontFamily && {
              "& .MuiTypography-root, button, textarea, input, .ql-editor p": {
                fontFamily: fontSettings.fontFamily,
              },
            },
            fontSettings?.bgColor && {
              backgroundColor: fontSettings.bgColor,
            },
            theme.palette.mode === "dark" && {
              "& .ql-editor": {
                backgroundColor: theme.palette.background.default,
              },
            },
          ],
        }}
      >
        <IconButton aria-label="close" onClick={close} sx={styles.closeButton}>
          <CloseIcon fontSize="large" />
        </IconButton>
        <Image name="notes" sx={styles.mainImage} />
        <DialogContent
          sx={[
            styles.content,
            fontSettings?.bgColor && {
              backgroundColor: fontSettings.bgColor,
            },
          ]}
        >
          <Stack
            id="notes-content-header"
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Stack
              direction={"row"}
              flex={1}
              justifyContent={"flex-start"}
              alignItems={"center"}
              spacing={1}
            >
              <Box
                display={"flex"}
                alignItems={"center"}
                position={"relative"}
                justifyContent={"flex-end"}
                sx={styles.searchBox}
              >
                <TextInput
                  placeholder={t("search")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onSubmit={onSearch}
                  InputProps={{
                    sx: styles.searchInput,
                    startAdornment:
                      search.length > 0 ? (
                        <InputAdornment position="start">
                          <IconButton
                            size="small"
                            sx={{ padding: 0 }}
                            onClick={cleanSearch}
                          >
                            <CloseIcon
                              fontSize="small"
                              sx={(theme) => ({
                                color: theme.palette.common.black,
                              })}
                            />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                  }}
                />
                <IconButton sx={styles.inputButton} onClick={onSearch}>
                  {t("go")}
                </IconButton>
              </Box>
            </Stack>
            <Box flex={1} display={"flex"} justifyContent={"center"}></Box>
            <Box flex={1} display={"flex"} justifyContent={"flex-end"}>
              <Typography variant={"span"} textTransform={"capitalize"}>
                {t("notes")}
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={0}
            mt={2}
            id="student-notes-body"
            overflow={"hidden"}
          >
            <Stack
              id="student-take-notes-body-left"
              direction={{
                xs: "row",
                sm: "column",
              }}
              flex={{
                xs: 1,
                sm: 1 / 4,
              }}
              sx={styles.notesBoxLeft}
            >
              {notes.map((note, index) => {
                return (
                  <NotesItem
                    onClick={() => selectItem(index)}
                    item={note}
                    key={index}
                    sx={styles.notesItem}
                  />
                )
              })}
            </Stack>
            <Stack
              id="student-take-notes-body-right"
              flex={1}
              sx={[styles.notesBoxRight]}
            >
              <Stack
                display={"row"}
                id="right-module-info"
                justifyContent={"space-between"}
                direction={"row"}
                alignItems={"center"}
              >
                <TextInput
                  placeholder={t("title")}
                  InputProps={{
                    sx: styles.searchInput,
                    disabled:
                      notes.find((i) => i.isSelected)?.noteTypeId > 0 ||
                      (notes.find((i) => i.isSelected)?.userIdCreatedBy !==
                        userData?.userId &&
                        !notes.find((i) => i.isSelected)?.allowEdit),
                  }}
                  value={
                    selectedItem !== null && selectedItem?.noteTypeId === 0
                      ? noteTitle
                      : noteTitle?.length > 0
                      ? t(
                          uiTranslationKey[selectedItem?.noteTypeId] ||
                            noteTitle
                        )
                      : noteTitle
                  }
                  onChange={(e) => setNoteTitle(e.target.value)}
                />
                <Stack id="stn-icons" direction={"row"} spacing={0.5}>
                  <FaEdit style={styles.icon} onClick={addNewNote} />
                  {notes.find((i) => i.isSelected)?.userIdCreatedBy ===
                    userData?.userId && (
                    <FaUserTag
                      style={styles.icon}
                      onClick={() => {
                        setOpenShare(true)
                      }}
                    />
                  )}
                  {/* <FaFileExport style={styles.icon} /> */}
                  {notes.find((i) => i.isSelected)?.allowDelete && (
                    <FaTrash
                      style={styles.icon}
                      onClick={() => {
                        setConfirm(true)
                      }}
                    />
                  )}
                </Stack>
              </Stack>
              <Stack className={"notes-editor"}>
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignContent={"center"}
                  pt={2}
                >
                  {(notes.find((i) => i.isSelected)?.noteId === 0 ||
                    notes.find((i) => i.isSelected)?.userIdCreatedBy ===
                      userData?.userId ||
                    notes.find((i) => i.isSelected)?.allowEdit) && (
                    <Button
                      sx={[styles.saveButton]}
                      disableElevation
                      variant="contained"
                      color="success"
                      onClick={saveNote}
                    >
                      {t("save")}
                    </Button>
                  )}

                  <Stack
                    flex={1}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <VirtualKeyboard
                      listenKeys={false}
                      fullKeyboard={false}
                      templateId={3}
                      useOneLine
                      keyStyle={styles.key(theme)}
                      onKeyPress={onKeyPress}
                    />
                  </Stack>
                </Stack>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={value}
                  onChange={setValue}
                  modules={{
                    toolbar: [
                      ["bold", "italic", "underline", "strike", "blockquote"],
                      [{ color: [] }, { background: [] }],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ align: [] }],
                      [{ header: [1, 2, 3, 4, 5, 6, false] }],
                      ["image"],
                    ],
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  )
}
